import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CalendarDays, Clock, Newspaper } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { PreferredSourceLink } from "@/components/site/PreferredSourceLink";
import { Section } from "@/components/site/Blocks";
import { buildHead } from "@/lib/seo-head";
import { Badge } from "@/components/ui/badge";
import { BLOG_CATEGORIES, formatBlogDate, sortedPosts } from "@/data/blog-posts";
import { PageOutro } from "@/components/site/PageEssentials";
import { BlogPostImage } from "@/components/blog/BlogPostImage";
import { listPublishedPosts, type PublicBlogPost } from "@/lib/blog-public.functions";

type Card = {
  slug: string;
  title: string;
  excerpt: string;
  category: string | null;
  publishedAt: string;
  readMinutes: number;
  image: string | null;
  imageAlt: string | null;
};

export const Route = createFileRoute("/blog/")({
  loader: async () => {
    try {
      const rows = (await listPublishedPosts({ data: {} })) as PublicBlogPost[];
      return { dbPosts: rows };
    } catch {
      return { dbPosts: [] as PublicBlogPost[] };
    }
  },
  head: () =>
    buildHead({
      title: "Investor Loan Blog | DSCR, Bridge & Rental Financing Guides",
      description:
        "Practical financing guides for rental property investors: DSCR underwriting, BRRRR, foreign national loans, Section 8 rent, and market rent data.",
      url: "/blog",
      keywords:
        "dscr loan blog, rental property financing guides, investor mortgage blog, brrrr financing, foreign national loan guide, section 8 rental income",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
      ],
      service: { name: "Investor property financing" },
    }),
  component: BlogIndex,
});

function BlogIndex() {
  const { dbPosts } = Route.useLoaderData() as { dbPosts: PublicBlogPost[] };
  const staticCards: Card[] = sortedPosts().map((p) => ({
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    category: p.category,
    publishedAt: p.publishedAt,
    readMinutes: p.readMinutes,
    image: null,
    imageAlt: null,
  }));
  const dbCards: Card[] = dbPosts.map((p) => ({
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt ?? "",
    category: p.category,
    publishedAt: p.published_at ?? p.updated_at,
    readMinutes: p.read_mins,
    image: p.hero_image_url ?? p.cover_url,
    imageAlt: p.cover_alt,
  }));
  const seen = new Set(dbCards.map((c) => c.slug));
  const posts = [...dbCards, ...staticCards.filter((c) => !seen.has(c.slug))].sort((a, b) =>
    a.publishedAt < b.publishedAt ? 1 : -1,
  );
  const featured = posts[0];
  const remaining = posts.slice(1);

  return (
    <SiteShell hideBreadcrumbs>
      <header className="border-b border-border bg-brand-gradient text-brand-foreground">
        <div className="container mx-auto max-w-7xl px-4 py-12 md:py-16">
          <nav aria-label="Breadcrumb" className="text-xs text-brand-foreground/70">
            <Link to="/" className="hover:text-brand-gold">Home</Link>
            <span className="mx-2" aria-hidden>›</span>
            <span className="text-brand-foreground">Blog</span>
          </nav>
          <div className="mt-7 max-w-4xl">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-gold">
              <Newspaper className="h-4 w-4" aria-hidden /> Investor intelligence
            </div>
            <h1 className="mt-4 font-display text-4xl font-bold leading-tight sm:text-5xl">
              Investor financing guides
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-brand-foreground/80 sm:text-lg">
              Straight answers on DSCR underwriting, rental property financing, market data, and
              preparing an investment property file for review.
            </p>
          </div>
        </div>
      </header>

      <Section className="!py-10 md:!py-14">
        <div className="flex flex-wrap gap-2" aria-label="Blog topics">
          {BLOG_CATEGORIES.map((category) => (
            <Badge key={category.slug} variant="outline" className="border-border bg-card px-3 py-1 text-foreground">
              {category.name}
            </Badge>
          ))}
        </div>

        {featured ? (
          <section className="mt-7" aria-labelledby="latest-guide-heading">
            <p className="text-xs font-bold uppercase tracking-widest text-brand">Latest guide</p>
            <Link
              to="/blog/$slug"
              params={{ slug: featured.slug }}
              className="group mt-3 grid min-w-0 overflow-hidden rounded-lg border border-border bg-card shadow-sm transition hover:border-brand-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 lg:grid-cols-[minmax(0,1.25fr)_minmax(22rem,0.75fr)]"
            >
              <div className="overflow-hidden bg-muted">
                <BlogPostImage
                  src={featured.image}
                  alt={featured.imageAlt}
                  title={featured.title}
                  category={featured.category}
                  eager
                  className="aspect-[16/9] h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                />
              </div>
              <div className="flex min-w-0 flex-col justify-center p-6 sm:p-8 lg:p-10">
                <p className="text-xs font-bold uppercase tracking-widest text-brand">
                  {BLOG_CATEGORIES.find((category) => category.slug === featured.category)?.name ?? "Investor guide"}
                </p>
                <h2 id="latest-guide-heading" className="mt-3 break-words font-display text-2xl font-bold leading-tight text-foreground sm:text-3xl group-hover:text-brand">
                  {featured.title}
                </h2>
                <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-muted-foreground sm:text-base">{featured.excerpt}</p>
                <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-brand" aria-hidden /> <time dateTime={featured.publishedAt}>{formatBlogDate(featured.publishedAt)}</time></span>
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-brand" aria-hidden /> {featured.readMinutes} min read</span>
                </div>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand">
                  Read the guide <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
                </span>
              </div>
            </Link>
          </section>
        ) : null}

        <section className="mt-14" aria-labelledby="all-guides-heading">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-brand">Research library</p>
              <h2 id="all-guides-heading" className="mt-2 font-display text-2xl font-bold text-foreground sm:text-3xl">More investor guides</h2>
            </div>
          </div>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {remaining.map((p) => {
            const cat = BLOG_CATEGORIES.find((c) => c.slug === p.category);
            return (
              <Link
                key={p.slug}
                to="/blog/$slug"
                params={{ slug: p.slug }}
                className="group flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm transition hover:-translate-y-0.5 hover:border-brand-gold hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <div className="overflow-hidden bg-muted">
                  <BlogPostImage
                    src={p.image}
                    alt={p.imageAlt}
                    title={p.title}
                    category={p.category}
                    className="aspect-[16/9] w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-brand">
                    {cat?.name ?? "Investor guide"}
                  </div>
                  <h3 className="mt-2 break-words text-lg font-bold leading-snug text-foreground group-hover:text-brand">
                    {p.title}
                  </h3>
                  <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-muted-foreground">{p.excerpt}</p>
                  <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border pt-4 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-brand" aria-hidden /> <time dateTime={p.publishedAt}>{formatBlogDate(p.publishedAt)}</time></span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-brand" aria-hidden /> {p.readMinutes} min</span>
                  </div>
                </div>
              </Link>
            );
          })}
          </div>
        </section>

        <PreferredSourceLink surface="blog-index" variant="card" className="mt-10 max-w-2xl" />
      </Section>
      <PageOutro hideLeadForm />
      </SiteShell>
  );
}
